import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const CATEGORIES = ['all', 'metalwork', 'print3d', 'engraving', 'scaffolding'];

// Inline safe text coercion (no external import to avoid bundling issues)
const toStr = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    if (v.cs) return v.cs;
    for (const k of Object.keys(v)) {
      if (typeof v[k] === 'string' && v[k]) return v[k];
    }
    return '';
  }
  return String(v);
};

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data } = await axios.get('/api/gallery');
      const safeItems = (Array.isArray(data) ? data : [])
        .map((item) => {
          const images = Array.isArray(item.images) && item.images.length
            ? item.images.filter((u) => typeof u === 'string' && u.trim())
            : (typeof item.image_url === 'string' && item.image_url.trim() ? [item.image_url] : []);
          return {
            id: item.id,
            title: toStr(item.title),
            description: toStr(item.description),
            category: typeof item.category === 'string' ? item.category : 'metalwork',
            images,
          };
        })
        .filter((item) => item.images.length > 0);
      setGallery(safeItems);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = activeCategory === 'all'
    ? gallery
    : gallery.filter((item) => (item.category || 'metalwork') === activeCategory);

  const openLightbox = (item) => {
    setSelectedItem(item);
    setSelectedIndex(0);
  };

  const closeLightbox = () => {
    setSelectedItem(null);
    setSelectedIndex(0);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setSelectedIndex((i) => (i - 1 + selectedItem.images.length) % selectedItem.images.length);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setSelectedIndex((i) => (i + 1) % selectedItem.images.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white dark:from-black dark:via-gray-900 dark:to-black transition-colors duration-200 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">{t('gallery.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">{t('gallery.subtitle')}</p>
          <div className="w-24 h-1 bg-green-600 dark:bg-green-500 mx-auto mt-6"></div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12" data-testid="gallery-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`gallery-tab-${cat}`}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-green-600 dark:bg-green-500 text-white dark:text-black shadow-lg shadow-green-500/30'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {t(`gallery.filters.${cat}`)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-20">...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-20">
            {t('gallery.empty')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div
                key={item.id}
                onClick={() => openLightbox(item)}
                data-testid={`gallery-item-${item.id}`}
                className="group cursor-pointer bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:border-green-500 transition-all duration-300 transform hover:-translate-y-2 shadow-lg"
              >
                <div className="aspect-square overflow-hidden relative">
                  <img
                    src={item.images[0]}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  {item.images.length > 1 && (
                    <div className="absolute top-3 right-3 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded-full">
                      +{item.images.length - 1}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-gray-900 dark:text-white font-semibold mb-1 line-clamp-1">{item.title}</h3>
                  {item.description && (
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">{item.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedItem && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={closeLightbox}
            data-testid="gallery-lightbox-close"
            className="absolute top-4 right-4 text-white hover:text-green-400 transition-colors duration-200 p-2 z-10"
          >
            <X size={32} />
          </button>

          {selectedItem.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                data-testid="lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-green-400 p-2 z-10 bg-black/40 rounded-full"
              >
                <ChevronLeft size={36} />
              </button>
              <button
                onClick={nextImage}
                data-testid="lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-green-400 p-2 z-10 bg-black/40 rounded-full"
              >
                <ChevronRight size={36} />
              </button>
            </>
          )}

          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedItem.images[selectedIndex]}
              alt={selectedItem.title}
              className="w-full h-auto rounded-xl shadow-2xl max-h-[80vh] object-contain"
            />
            <div className="mt-4 text-center text-white">
              <h3 className="text-xl font-semibold">{selectedItem.title}</h3>
              {selectedItem.description && (
                <p className="text-gray-300 mt-2">{selectedItem.description}</p>
              )}
              {selectedItem.images.length > 1 && (
                <p className="text-sm text-gray-400 mt-2">
                  {selectedIndex + 1} / {selectedItem.images.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
